import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { KundeProfile } from '../types';
import * as authService from '../services/authService';
import { getAccessToken } from '../services/shopifyAuth';

interface AuthState {
  user: KundeProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: getAccessToken(),
  isAuthenticated: getAccessToken() !== null,
  loading: false,
  error: null,
};

export const fetchProfile = createAsyncThunk<
  KundeProfile,
  void,
  { rejectValue: string }
>('auth/fetchProfile', async (_, { rejectWithValue }) => {
  try {
    return await authService.getCurrentUser();
  } catch (error: any) {
    return rejectWithValue('Kunne ikke hente profil');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Called from the OAuth callback page once Shopify tokens are stored
    setSession(state, action: PayloadAction<string>) {
      state.token = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    logout(state) {
      authService.logoutUser();
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearAuthError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchProfile.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        authService.logoutUser();
      });
  },
});

export const { setSession, logout, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
